import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  OnDestroy,
  Output,
  ViewChild,
  HostListener,
  ElementRef,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { EventService } from 'src/app/core/services/event.service';
import { EventType } from 'src/app/core/constants/events';
import { ProfileOptionItem } from '../models/profileoption.model';
import { getProfileOptions } from './data';
import { PageTitle } from '../models/page-title.model';
import { LayoutColor } from '../config/layout.model';
import { UtilitiesService } from '../../../core/services/utilities.service';
import { TranslateService } from '@ngx-translate/core';
import { AlertService } from '../../../core/services/alert.service';
import { NotificationItem } from '../models/notification.model';
import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap';
import { NotificationsResponse } from '../models/notification.model';
import { Provider } from 'src/app/core/models/users.model';
import { UsersService } from 'src/app/core/services/users.service';
import { Router } from '@angular/router';

@Component({
  selector: 'app-topbar',
  templateUrl: './topbar.component.html',
  styleUrls: ['./topbar.component.scss'],
})
export class TopbarComponent implements OnInit, OnDestroy {
  profileOptions: ProfileOptionItem[] = [];
  pageTitle: string = '';
  topnavCollapsed: boolean = false;
  normalLogo = '';
  smallNormalLogo = '';
  darkLogo = '';
  smallDarkLogo = '';
  @Input() layoutColor!: string;
  @Input() layoutType: string = 'vertical';
  @Input() containerClass?: string = '';
  @Output() mobileMenuButtonClicked = new EventEmitter<void>();
  @ViewChild('notificationsDropdown') notificationsDropdown!: NgbDropdown;
  @ViewChild('notificationsDropdownContainer', { read: ElementRef })
  notificationsDropdownContainer!: ElementRef;
  tourState = false;
  tourStateValue = '';
  currentUser: any;
  notifications: NotificationItem[] = [];
  notificationsTimer: any;
  notificationsPerPage: number = 5;
  notificationsCurrentPage: number = 1;
  notificationsUpdatedSubscription?: Subscription;
  tenants: Provider[] = [];
  selectedTenant: number = 0;
  isAdmin: boolean = false;

  constructor(
    private eventService: EventService,
    private utilitiesService: UtilitiesService,
    private translateService: TranslateService,
    private alertService: AlertService,
    private usersService: UsersService,
    private router: Router
  ) {
    this.eventService
      .on(EventType.CHANGE_PAGE_TITLE)
      .subscribe(({ payload }) => {
        this.pageTitle = (payload as PageTitle).title;
      });
  }

  ngOnInit(): void {
    this._fetchProfileOptions();
    this.currentUser = JSON.parse(sessionStorage.getItem('cayc-user')!);
    this._initializeSelectedTenant();

    const storedColorScheme = localStorage.getItem('cayc-ui-theme');
    if (storedColorScheme) {
      this.changeColorScheme(storedColorScheme);
    }
    const storedTourState = localStorage.getItem('cayc-tour');
    if (storedTourState) {
      this.tourState = storedTourState === 'ON';
      this.tourStateValue = storedTourState;
    }

    if (this.isNotificationPushEnabled()) {
      this._fetchNotifications();
      this.notificationsTimer = setInterval(() => {
        this._fetchNotifications();
      }, 20000);

      // Suscribirse a actualizaciones de notificaciones
      this.notificationsUpdatedSubscription =
        this.utilitiesService.notificationsUpdated$.subscribe(() => {
          this._fetchNotifications();
        });
    }
  }

  ngOnDestroy(): void {
    if (this.notificationsTimer) {
      clearInterval(this.notificationsTimer);
    }
    if (this.notificationsUpdatedSubscription) {
      this.notificationsUpdatedSubscription.unsubscribe();
    }
  }

  /**
   * Mostrar/ocultar el tour
   */
  public toggleTour() {
    this.utilitiesService.toggleTour().subscribe(
      (response) => {
        if (response.data.status === 'desactivado') {
          this.tourState = true;
          this.tourStateValue = 'ON';
          localStorage.setItem('cayc-tour', 'ON');
        } else {
          this.tourState = false;
          this.tourStateValue = 'OFF';
          localStorage.setItem('cayc-tour', 'OFF');
        }
        window.location.reload();
      },
      (error) => {
        this.alertService.showAlert({
          type: 'danger',
          message: this.translateService.instant('layout.topbar.tour_error'),
          show: true,
        });
      }
    );
  }

  /**
   * Obtener opciones de perfil
   */
  _fetchProfileOptions(): void {
    this.profileOptions = getProfileOptions(this.translateService);
  }

  /**
   * Mostrar/ocultar la barra de menú en dispositivos móviles
   */
  toggleMobileMenu(event: any) {
    this.topnavCollapsed = !this.topnavCollapsed;
    event.preventDefault();
    this.mobileMenuButtonClicked.emit();
  }

  /**
   * Obtener las iniciales del usuario
   * @returns Iniciales del usuario en mayúsculas
   */
  getUserInitials(): string {
    if (this.currentUser?.name && this.currentUser?.surname) {
      const initials = this.currentUser.name[0] + this.currentUser.surname[0];
      return initials.toUpperCase();
    }
    return '';
  }

  /**
   * Cambiar el esquema de color del layout entre claro y oscuro
   */
  toggleColorScheme(): void {
    const newColorScheme = this.layoutColor === 'dark' ? 'light' : 'dark';
    this.changeColorScheme(newColorScheme);
    localStorage.setItem('cayc-ui-theme', newColorScheme);
  }

  /**
   * Cambiar el esquema de color del layout
   * @param color Nombre del esquema de color a aplicar
   */
  changeColorScheme(color: string): void {
    this.layoutColor = color;
    this.eventService.broadcast(
      EventType.CHANGE_LAYOUT_COLOR,
      color as LayoutColor
    );
  }

  /**
   * Manejar clics fuera del dropdown de notificaciones
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (
      this.notificationsDropdownContainer &&
      this.notificationsDropdown?.isOpen()
    ) {
      const clickedInside =
        this.notificationsDropdownContainer.nativeElement.contains(
          event.target as Node
        );
      if (!clickedInside) {
        this.notificationsDropdown.close();
      }
    }
  }

  /**
   * Verificar si las notificaciones push están habilitadas
   * @returns true si las notificaciones push están habilitadas
   */
  private isNotificationPushEnabled(): boolean {
    const currentUser = JSON.parse(
      sessionStorage.getItem('cayc-user') || 'null'
    );
    return currentUser?.notificationPush === true;
  }

  /**
   * Obtener las notificaciones
   */
  _fetchNotifications(
    queryParams: any = {
      limit: this.notificationsPerPage,
      page: this.notificationsCurrentPage,
    }
  ): void {
    if (!this.isNotificationPushEnabled()) {
      return;
    }
    this.utilitiesService
      .getNotifications(queryParams)
      .subscribe((response: NotificationsResponse) => {
        this.notifications = response.data.items.map(
          (notification: NotificationItem) => ({
            id: notification.id,
            title: notification.title,
            description: notification.description,
            isRead: notification.isRead ?? false,
            createdAt: new Date(notification.createdAt),
            type: notification.type,
          })
        );
      });
  }

  /**
   * Marcar como leido una notificación
   * @param id ID de la notificación
   * @param event Evento del clic
   */
  markAsRead(id: number, event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    }
    this.utilitiesService.markAsRead(id).subscribe();
  }

  /**
   * Obtener el número de notificaciones no leídas
   * @returns Número de notificaciones no leídas
   */
  getUnreadNotificationsCount(): number {
    return this.notifications.filter((notification) => !notification.isRead)
      .length;
  }

  /**
   * Obtener solo las notificaciones no leídas
   * @returns Array de notificaciones no leídas
   */
  getUnreadNotifications(): NotificationItem[] {
    return this.notifications.filter((notification) => !notification.isRead);
  }

  /**
   * Marcar todas las notificaciones como leídas
   * @param event Evento del clic
   */
  markAllAsRead(event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    }
    this.utilitiesService.markAllAsRead().subscribe({
      next: () => {
        if (this.notificationsDropdown?.isOpen()) {
          this.notificationsDropdown.close();
        }
      },
    });
  }

  /**
   * Cerrar el dropdown de notificaciones
   */
  closeNotificationsDropdown(): void {
    if (this.notificationsDropdown?.isOpen()) {
      this.notificationsDropdown.close();
    }
  }

  /**
   * Mostrar el selector de tenants
   * @returns true si el usuario es admin y tiene más de un proveedor
   */
  showTenantSelector(): boolean {
    if (this.currentUser?.isAdmin && this.currentUser?.providers?.length >= 1) {
      this.getTenants();
      return true;
    }
    return false;
  }

  /**
   * Inicializar el tenant seleccionado
   */
  private _initializeSelectedTenant(): void {
    if (this.currentUser?.providers && this.currentUser.providers.length > 0) {
      const selectedProvider = this.currentUser.providers.find(
        (provider: Provider) => provider.selected === true
      );
      if (selectedProvider) {
        this.selectedTenant = selectedProvider.id;
      }
    }
  }

  /**
   * Obtener los tenants
   * @returns Array de tenants
   */
  getTenants(): Provider[] {
    this.tenants = this.currentUser?.providers || [];
    if (this.tenants.length > 0 && !this.selectedTenant) {
      this._initializeSelectedTenant();
    }
    return this.tenants;
  }

  /**
   * Obtener el nombre del tenant seleccionado
   * @returns Nombre del tenant seleccionado
   */
  getSelectedTenantName(): string {
    if (this.selectedTenant === 0) {
      return this.translateService.instant('layout.topbar.select_tenant');
    }
    const selected = this.tenants.find(
      (tenant: Provider) => tenant.id === this.selectedTenant
    );
    return (
      selected?.name ||
      this.translateService.instant('layout.topbar.select_tenant')
    );
  }

  /**
   * Seleccionar un tenant
   * @param tenantId ID del tenant a seleccionar
   */
  selectTenant(tenantId: number): void {
    this.selectedTenant = tenantId;
    this.usersService.setDefaultTenant(tenantId).subscribe(
      (response: any) => {
        this.alertService.showAlert({
          type: 'success',
          message: this.translateService.instant(
            'layout.topbar.tenant_selected'
          ),
          show: true,
        });
        const currentUser = JSON.parse(sessionStorage.getItem('cayc-user')!);
        if (currentUser !== null) {
          currentUser.providers.forEach((provider: Provider) => {
            provider.selected = provider.id === tenantId;
          });
          sessionStorage.setItem('cayc-user', JSON.stringify(currentUser));
        }
        window.location.reload();
      },
      (error: any) => {
        this.alertService.showAlert({
          type: 'danger',
          message: this.translateService.instant(
            'layout.topbar.tenant_selection_error'
          ),
          show: true,
        });
      }
    );
  }

  /**
   * Navegar a la sección correspondiente a la notificación
   * @param id ID de la notificación
   */
  navigateToSectionByNotificationType(type: string): void {
    switch (type) {
      case 'Certificate':
        this.router.navigate(['/certificates']);
        break;
      case 'RelationLeasing':
        this.router.navigate(['/upload']);
        break;
    }
    this.notificationsDropdown.close();
  }
}
